import {
  AnalysisFeedbackPayload,
  ChangeReasonBanner,
  ConfidenceIndicator,
  CriterionDetailedChecklist,
  EvidenceCardConfig,
} from "@risalabs_frontend_org/oasis-ui-kit";
import React from "react";

import { QuestionForUI } from "../../data-model/cmmMedicalNeccessityQuestionnaire";

import type { QuestionPatch } from "./index";
import UserFeedBackOnAnswerChange from "./userFeedbackForAnswerChange";

export interface ChecklistQuestionProps {
  question: QuestionForUI;
  stepNumber: number;
  onChange: (patch: QuestionPatch) => void;
  disabled?: boolean;
  evidenceCards?: EvidenceCardConfig[];
  confidenceScore?: number;
  answerChangedReason?: string;
  shouldShowUserFeedBack?: boolean;
  onSubmit?: (
    payload: AnalysisFeedbackPayload & { questionName: string },
  ) => void;
  onCancel?: (payload: { questionName: string }) => void;
}

/**
 * Renders a `multi-subjective` question: a checklist of options plus an
 * optional or mandatory follow-up textarea. The `answer` is a `string[]` of
 * checked values; a single shared `free_text_answer` accompanies the selection.
 */
export const ChecklistQuestion: React.FC<ChecklistQuestionProps> = ({
  question,
  stepNumber,
  onChange,
  disabled,
  evidenceCards,
  confidenceScore,
  answerChangedReason,
  shouldShowUserFeedBack = false,
  onSubmit,
  onCancel,
}) => {
  const handleSubmit = (payload: AnalysisFeedbackPayload) => {
    onSubmit?.({ ...payload, questionName: question.name });
  };
  const handleCancel = () => {
    onCancel?.({ questionName: question.name });
  };
  const checkedValues = Array.isArray(question.answer) ? question.answer : [];
  const shouldShowChangeReasonBanner =
    answerChangedReason && answerChangedReason.trim().length > 0;
  return (
    <CriterionDetailedChecklist
      id={question.name}
      stepNumber={stepNumber}
      title={question.question}
      checklistName={question.name}
      checklistOptions={question.options}
      checkedValues={checkedValues}
      checklistDisabled={disabled}
      onCheckChange={(_value, _checked, allChecked) =>
        onChange({ name: question.name, answer: allChecked })
      }
      textareaName={question.name}
      textareaValue={question.free_text_answer.answer}
      textareaDisabled={disabled}
      shouldHideTextBox={!question.shouldShowTextArea}
      isTextRequired={question.isTextAreaMandatory}
      onTextChange={(value) =>
        onChange({
          name: question.name,
          free_text_answer: {
            name: question.free_text_answer.name,
            answer: value,
          },
        })
      }
      evidenceCards={evidenceCards}
      badge={
        <ConfidenceIndicator label="CONFIDENCE" value={confidenceScore ?? 0} />
      }
      headerSlot={
        shouldShowChangeReasonBanner ? (
          <ChangeReasonBanner
            label="ANSWER CHANGED REASON"
            reason={answerChangedReason}
            multiline={true}
          />
        ) : null
      }
      children={
        shouldShowUserFeedBack ? (
          <UserFeedBackOnAnswerChange
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        ) : null
      }
    />
  );
};

export default ChecklistQuestion;
